import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Editor } from '@tiptap/react'
import { EditorType } from '../types'
import { normalize } from '../../../../../../utils/multilineText'
import {
  getMarkdownFromSourceEditor,
  insertLinkFromClipboard,
  markdownToSourceDoc,
} from '../../../richtext'
import { isImageFile, UploadedFile } from '../../../../../../utils/file'
import TenantStorage from '../../../../../../utils/storage'
import store from '../../../../../../store'
import { showAlert } from '../../../../../../store/globalAlert'

type UseMultilineEditorParams = {
  editorType: EditorType
  initialized: boolean
  value: string
  dataLockVersion?: number
  setValue?: Function
  formatMessage: (descriptor: { id: string }) => string
}

const executeUpload = async (file): Promise<UploadedFile> => {
  const response = await TenantStorage.uploadFile(file)
  return {
    url: response.json.downloadUrl,
    name: file.name,
    mimeType: file.type,
  }
}

export const useMultilineEditor = ({
  editorType,
  initialized,
  value,
  dataLockVersion,
  setValue,
  formatMessage,
}: UseMultilineEditorParams) => {
  // 各 View が useEditor で生成した Editor インスタンスをマウント時に登録する。
  // モード切替で非アクティブ側の View はアンマウントされるため、同時に生存するのは原則1つ。
  const [richTextEditor, setRichTextEditor] = useState<Editor | null>(null)
  const [markdownSourceEditor, setMarkdownSourceEditor] =
    useState<Editor | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [previewText, setPreviewText] = useState<string>(normalize(value || ''))

  // 現在のMarkdown値のキャッシュ。親への通知や、モード切替時の同期に使う。
  const valueRef = useRef<string>(normalize(value || ''))

  const notifyChange = useCallback(
    (val: string) => {
      // valueRef は useEffect 内で normalize(props.value) と比較される。
      // tiptapの@tiptap/markdownが空段落を `&nbsp;` で表現するため、normalize で揃えないと
      // raw（&nbsp;あり）vs normalized（空白）の不一致でsetContentが走り、cursorが冒頭に戻る。
      valueRef.current = normalize(val)
      setValue?.(val)
      setPreviewText(val)
    },
    [setValue]
  )

  const debouncedNotifyChange = useMemo(
    () => _.debounce(notifyChange, 100),
    [notifyChange]
  )

  // マークダウン文字列を、指定モードのエディタに反映する。
  // emitUpdate: false により、setContent 起因の onUpdate 発火（=親への不要な setValue 通知）を抑止する。
  // 通知が必要なケース（テンプレート挿入等）は呼び出し側で明示的に notifyChange を呼ぶ想定。
  const applyMarkdownToEditor = useCallback(
    (md: string, type: EditorType) => {
      if (type === EditorType.Draft && richTextEditor) {
        richTextEditor.commands.setContent(md, {
          contentType: 'markdown',
          emitUpdate: false,
        })
      } else if (type === EditorType.Markdown && markdownSourceEditor) {
        markdownSourceEditor.commands.setContent(markdownToSourceDoc(md), {
          emitUpdate: false,
        })
      }
    },
    [richTextEditor, markdownSourceEditor]
  )

  // View がエディタを登録した直後に現在値を反映する。
  // モード切替時は新 View のマウントで新エディタが登録されるので、その時点でもここが走る。
  useEffect(() => {
    if (!initialized) return
    applyMarkdownToEditor(valueRef.current, editorType)
    if (editorType === EditorType.Draft) {
      richTextEditor?.commands.focus('start')
    } else {
      markdownSourceEditor?.commands.focus('start')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- エディタ登録のタイミングだけで反映したい
  }, [initialized, richTextEditor, markdownSourceEditor])

  // 親からのprops.value更新を検知して、アクティブなエディタに反映
  useEffect(() => {
    const newValue = normalize(value || '')
    if (newValue === valueRef.current) return
    valueRef.current = newValue
    setPreviewText(newValue)
    applyMarkdownToEditor(newValue, editorType)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props起因の更新のみ検知
  }, [value, dataLockVersion])

  const switchEditor = useCallback(() => {
    // モード切替前にアクティブエディタの最新マークダウンを valueRef に確保する。
    // 新エディタ側への反映は、新 View がマウントしてエディタを登録した後に
    // 上の useEffect（[initialized, richTextEditor, markdownSourceEditor]）で行われる。
    let currentMarkdown = valueRef.current
    if (editorType === EditorType.Draft && richTextEditor) {
      currentMarkdown = richTextEditor.getMarkdown()
    } else if (editorType === EditorType.Markdown && markdownSourceEditor) {
      currentMarkdown = getMarkdownFromSourceEditor(markdownSourceEditor)
    }
    valueRef.current = normalize(currentMarkdown)
    setPreviewText(currentMarkdown)
  }, [editorType, richTextEditor, markdownSourceEditor])

  const applyContent = useCallback(
    (text: string) => {
      notifyChange(text)
      applyMarkdownToEditor(text, editorType)
    },
    [notifyChange, applyMarkdownToEditor, editorType]
  )

  const hasContent = useCallback(() => !!valueRef.current, [])

  // --- Image upload ---

  const uploadImages = async (files): Promise<UploadedFile[]> => {
    const imgFiles = files.filter(file => isImageFile(file))
    if (files.length !== imgFiles.length) {
      store.dispatch(
        showAlert({
          message: formatMessage({
            id: 'editor.canNotUploadFile',
          }),
        })
      )
    }
    const uploadedFiles: UploadedFile[] = await Promise.all(
      imgFiles.map(file => executeUpload(file))
    )
    return uploadedFiles.filter(v => v.url)
  }

  const finalizeRichTextImageInsert = _.debounce(() => {
    if (!richTextEditor) return
    const md = richTextEditor.getMarkdown()
    setLoading(false)
    notifyChange(md)
  }, 100)

  const insertImagesToRichText = (files: UploadedFile[]) => {
    files.forEach(file => {
      richTextEditor?.chain().focus().setImage({ src: file.url }).run()
    })
    finalizeRichTextImageInsert()
  }

  const insertImagesToMarkdownSource = (files: UploadedFile[]) => {
    if (!markdownSourceEditor) return
    const imageMarkdown = files.reduce(
      (acc, value) => acc + `![](${value.url})\n\n`,
      ''
    )
    const currentText = getMarkdownFromSourceEditor(markdownSourceEditor)
    const newText = currentText
      ? `${currentText}\n${imageMarkdown}`
      : imageMarkdown
    markdownSourceEditor.commands.setContent(markdownToSourceDoc(newText), {
      emitUpdate: false,
    })
    setLoading(false)
    notifyChange(newText)
  }

  const onDropAcceptedDraft = async acceptedFiles => {
    const files = await uploadImages(acceptedFiles)
    insertImagesToRichText(files)
  }

  const onDropAcceptedMarkdown = async acceptedFiles => {
    const files = await uploadImages(acceptedFiles)
    insertImagesToMarkdownSource(files)
  }

  const onPaste = async event => {
    if (event.clipboardData.files.length) {
      const files = await uploadImages(Array.from(event.clipboardData.files))
      insertImagesToRichText(files)
    }
  }

  const onPasteCaptureIntoRichText = useCallback(
    event => {
      if (!richTextEditor || !event.clipboardData) return
      if (insertLinkFromClipboard(event.clipboardData, richTextEditor)) {
        event.preventDefault()
        event.stopPropagation()
      }
    },
    [richTextEditor]
  )

  return {
    previewText,
    loading,
    switchEditor,
    applyContent,
    hasContent,
    onDropAcceptedDraft,
    onDropAcceptedMarkdown,
    onPaste,
    onPasteCaptureIntoRichText,
    // View からの登録/通知用ハンドル
    registerRichTextEditor: setRichTextEditor,
    registerMarkdownSourceEditor: setMarkdownSourceEditor,
    onContentChange: debouncedNotifyChange,
  }
}
